// React hook for managing the current user's pending invitations
// Fetches invitations and provides accept/decline mutations

import { useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listPendingInvitations,
  acceptInvitation as acceptInvitationSdk,
  declineInvitation as declineInvitationSdk,
} from "@/services/api/sdk.gen";
import { logger } from "@/services/logger";
import type { PendingInvitation } from "@/types/sharing";

export const useInvitations = () => {
  const queryClient = useQueryClient();

  // Fetch pending invitations for the current user
  const invitationsQuery = useQuery({
    queryKey: ["pending-invitations"],
    queryFn: async (): Promise<PendingInvitation[]> => {
      const result = await listPendingInvitations();
      const body = result.data as Record<string, unknown> | undefined;
      const data = body?.data as PendingInvitation[] | undefined;
      return data ?? [];
    },
    staleTime: 60_000,
    refetchInterval: 5 * 60_000, // Poll every 5 minutes
  });

  // Accept invitation
  const acceptMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      const result = await acceptInvitationSdk({
        path: { invitationId },
      });
      if (result.error) {
        throw result.error;
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-invitations"] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["teams"] });
    },
    onError: (error: Error) => {
      logger.error("Failed to accept invitation", error);
    },
  });

  // Decline invitation (POST, not DELETE)
  const declineMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      const result = await declineInvitationSdk({
        path: { invitationId },
      });
      if (result.error) {
        throw result.error;
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-invitations"] });
    },
    onError: (error: Error) => {
      logger.error("Failed to decline invitation", error);
    },
  });

  const acceptInvitation = useCallback(
    (invitationId: string) => acceptMutation.mutateAsync(invitationId),
    [acceptMutation.mutateAsync],
  );

  const declineInvitation = useCallback(
    (invitationId: string) => declineMutation.mutateAsync(invitationId),
    [declineMutation.mutateAsync],
  );

  return {
    invitations: invitationsQuery.data ?? [],
    isLoading: invitationsQuery.isLoading,
    acceptInvitation,
    declineInvitation,
    isAccepting: acceptMutation.isPending,
    isDeclining: declineMutation.isPending,
  };
};
